import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useAwayIdeas from '../../hooks/useAwayIdeas';
import useToast from '../../hooks/useToast';
import { invalidateDashboardCache } from '../../hooks/useHomeDashboard';
import { invalidatePlannerCache } from '../../hooks/plannerCache';
import { invalidateTripCache } from '../../hooks/useTripState';
import Button from '../shared/Button';
import EmptyState from '../shared/EmptyState';
import PhotoCredit from '../shared/PhotoCredit';
import PoweredByGoogle from '../shared/PoweredByGoogle';
import Shimmer from '../shared/Shimmer';
import Toast from '../shared/Toast';
import CardGroup, { STRIP_SLOT } from './CardGroup';
import HomeSectionHeader from './HomeSectionHeader';
import { autoNormalizeCardData, CONTENT_TYPES } from '../../utils/normalizeCardData';
import { feedItemKey } from '../../utils/feedItemKey';
import { sanitizeUrl } from '../../utils/sanitizeUrl';
import { resizeForSurface } from '../../utils/imageSource';
import { buildPhotoSrcSet } from '../../utils/photoSrcSet';
import { formatDateRangeShort, formatTime, getLocalDateString, parseLocalDate, getDisplayLocale } from '../../utils/dateUtils';
import { ideaTargetDate, tripDayNumber, tripPlaceName } from '../../utils/homePlans';

const AddToTripModal = lazy(() => import('../TripPlanner/AddToTripModal'));

const CHIP_LIMIT = 4;
const CARD_LIMIT = 8;
const IMAGE_SIZING = '(max-width: 640px) 80vw, 280px';

const EVENT_TYPES = new Set([CONTENT_TYPES.EVENT, CONTENT_TYPES.EVENT_GROUP, CONTENT_TYPES.ARTIST_EVENT]);
const TOUR_TYPES = new Set([CONTENT_TYPES.WALKING_TOUR, CONTENT_TYPES.TOURISM_EXPERIENCE, CONTENT_TYPES.VIATOR_EXPERIENCE, CONTENT_TYPES.TIQETS_EXPERIENCE]);

/**
 * The payload POST /api/trips/:id/activities reads (tripActivityFields):
 * a tour by the field that names its kind (a feed row carries only
 * content_type, which the server doesn't read), an event by its event id,
 * a place by its spine id, and the title under both names the modal and the
 * route look for.
 */
export function toTripActivity(item, normalized) {
  const title = normalized.title;
  const base = { ...item, title, name: title, imageUrl: item.cover_photo || normalized.carousel?.images?.[0]?.url || null };
  switch (normalized.type) {
    case CONTENT_TYPES.WALKING_TOUR:
      return { ...base, tourId: item.id, category: 'walking_tour' };
    case CONTENT_TYPES.TOURISM_EXPERIENCE:
      return { ...base, experienceId: item.id, category: 'tourism_experience' };
    case CONTENT_TYPES.VIATOR_EXPERIENCE:
      return { ...base, viatorProductCode: item.product_code || item.productCode || item.id, category: 'viator_experience' };
    case CONTENT_TYPES.TIQETS_EXPERIENCE:
      return { ...base, tiqetsProductCode: item.product_code || item.experience_id || item.id, category: 'tiqets_experience' };
    case CONTENT_TYPES.EVENT:
    case CONTENT_TYPES.ARTIST_EVENT:
      return { ...base, eventId: item.id, event_id: item.id };
    default:
      return { ...base, placeId: item.attraction_id || item.place_id || item.placeId || item.id };
  }
}

function openDetail(normalized, navigate) {
  if (!normalized.detailUrl) return;
  if (normalized.externalLink) {
    window.open(sanitizeUrl(normalized.detailUrl), '_blank', 'noopener,noreferrer');
    return;
  }
  navigate(normalized.detailUrl);
}

function IdeaCard({ item, trip, dated, today, added, onAdd, onSave }) {
  const { t } = useTranslation('home');
  const navigate = useNavigate();
  const normalized = autoNormalizeCardData(item);
  const isEvent = EVENT_TYPES.has(normalized.type);
  // A run's card names its representative show, which may fall before the
  // trip; the show on the trip's dates isn't in the row, so a run is saved,
  // never dropped onto a day blind.
  const isRun = normalized.type === CONTENT_TYPES.EVENT_GROUP;
  const photo = normalized.carousel?.images?.[0]?.url || item.cover_photo || null;
  const sized = photo ? resizeForSurface(photo, 'medium') : null;
  const srcSet = photo ? buildPhotoSrcSet(photo) : null;

  // Where it lands: a dated event on its own day (when that day is on the
  // trip); anything else on the first empty day from today.
  const eventDate = isEvent && !isRun ? (item.event_date || '').slice(0, 10) : '';
  const onTrip = eventDate && tripDayNumber(trip, eventDate) ? eventDate : null;
  const targetDate = dated ? (onTrip || ideaTargetDate(trip, today)) : null;
  const dayNumber = targetDate ? tripDayNumber(trip, targetDate) : null;
  // The route recognises an event only by a `tm_`/`event_` id
  // (tripActivityFields); a UiT or Paris event would be stored as a place
  // stop with no title, so until that rule reads eventId those are saved.
  const addable = !isEvent || /^(tm_|event_)/.test(String(item.id || ''));
  const canAdd = !!dayNumber && !isRun && addable;

  const kindLabel = isEvent
    ? (normalized.typeData?.categoryLabel || t('away.kind.event'))
    : normalized.type === CONTENT_TYPES.WALKING_TOUR ? t('away.kind.walk')
      : TOUR_TYPES.has(normalized.type) ? t('away.kind.tour')
        : (normalized.typeData?.categoryLabel || t('away.kind.place'));

  const meta = isEvent
    ? [item.event_time ? formatTime(item.event_time) : null, item.venue_name || item.venue || item.city].filter(Boolean).join(t('hero.separator'))
    : [normalized.location || item.city, normalized.typeData?.stopCount ? t('away.stops', { count: normalized.typeData.stopCount }) : null].filter(Boolean).join(t('hero.separator'));

  const dayChip = onTrip
    ? t('away.onYourDay', { date: parseLocalDate(onTrip).toLocaleDateString(getDisplayLocale(), { weekday: 'short', day: 'numeric' }), index: dayNumber })
    : null;

  // A time-less dated event opens the sheet with no start picked, never at an
  // invented free slot (AddToTripModal's eventStart contract, as the Map tab
  // does it); an ongoing one takes the suggested slot like a place.
  const eventStart = isEvent && !isRun && !item.is_ongoing
    ? { time: String(item.event_time || '').slice(0, 5) || null }
    : null;

  return (
    <article className="relative h-full bg-surface rounded-card border border-border overflow-hidden flex flex-col can-hover:hover:border-content-muted transition-colors duration-fast">
      <div className="relative aspect-[4/3] bg-background overflow-hidden">
        {sized && <img src={sized} srcSet={srcSet || undefined} sizes={srcSet ? IMAGE_SIZING : undefined} alt="" className="w-full h-full object-cover" loading="lazy" />}
        {dayChip && <span className="absolute top-2.5 left-2.5 z-raised px-2 h-6 inline-flex items-center rounded-full bg-coral-light text-coral-dark text-xs font-bold">{dayChip}</span>}
        {sized && <PhotoCredit src={sized} size="md" />}
      </div>
      <div className="p-3 flex flex-col gap-1 flex-grow">
        <span className="self-start px-2 h-5 inline-flex items-center rounded-full bg-surface-secondary text-content-secondary text-[11px] font-semibold">{kindLabel}</span>
        <h3 className="text-[15px] font-semibold text-content-primary line-clamp-2 mt-0.5">{normalized.title}</h3>
        {meta && <p className="text-xs text-content-secondary line-clamp-1">{meta}</p>}
        <div className="mt-auto pt-2 relative z-raised">
          {added ? (
            <Button variant="secondary" size="sm" disabled>{t('away.addedShort', { index: added })}</Button>
          ) : canAdd ? (
            <Button variant="secondary" size="sm" onClick={() => onAdd(toTripActivity(item, normalized), targetDate, eventStart)}>
              {t('away.addToDay', { index: dayNumber })}
            </Button>
          ) : (
            <Button variant="secondary" size="sm" onClick={() => onSave(item)}>{t('away.saveIdea')}</Button>
          )}
        </div>
      </div>
      {normalized.detailUrl && (
        <button
          type="button"
          onClick={() => openDetail(normalized, navigate)}
          aria-label={t('away.open', { title: normalized.title })}
          className="absolute inset-0 w-full h-full rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-light"
        />
      )}
    </article>
  );
}

function Skeleton() {
  const { t } = useTranslation('home');
  return (
    <CardGroup cols={4}>
      <span className="sr-only" role="status">{t('dashboardSkeleton.loadingFeed')}</span>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`${STRIP_SLOT} bg-surface rounded-card border border-subtle overflow-hidden animate-pulse`}>
          <div className="aspect-[4/3] bg-background" />
          <div className="p-3 space-y-2"><Shimmer className="h-4 w-3/4 bg-background rounded-lg" /><Shimmer className="h-3 w-1/2 bg-background rounded-lg" /></div>
        </div>
      ))}
    </CardGroup>
  );
}

/**
 * "While you're in Lisbon": things to do at the destinations you've planned —
 * events on during the trip's dates, places and walks near where you'll
 * stay — each with "Add to day N" aimed at the first empty day from today.
 * Chips switch between the trips with a place (buildPlans' `away`, hero's
 * trip first); `focusTrip` ({ id, nonce }) selects one from outside (the
 * hero's "Ideas for day N"). `suppressKeys` (feed keys) are cards already on
 * screen above — This weekend's, when both are about where you are now.
 */
export default function WhileAwaySection({ away, buildActions, onAdded, sectionRef, focusTrip = null, suppressKeys = null }) {
  const { t } = useTranslation('home');
  const { currentUser } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const today = getLocalDateString(new Date());

  const [selectedId, setSelectedId] = useState(away[0]?.trip.id || null);
  useEffect(() => {
    if (!away.some(a => a.trip.id === selectedId)) setSelectedId(away[0]?.trip.id || null);
  }, [away, selectedId]);
  useEffect(() => {
    if (focusTrip?.id && away.some(a => a.trip.id === focusTrip.id)) setSelectedId(focusTrip.id);
  // Re-run per jump (nonce), not per away change.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTrip?.id, focusTrip?.nonce]);
  const selected = useMemo(() => away.find(a => a.trip.id === selectedId) || away[0] || null, [away, selectedId]);

  const ideas = useAwayIdeas(selected, today);
  const [adding, setAdding] = useState(null); // { activity, date, eventStart }
  // Cards already dropped onto a day this visit: shown as added, not offered
  // again (nothing on the server dedupes a second add).
  const [addedByKey, setAddedByKey] = useState({});

  if (!selected) return null;
  const { trip, center, stage } = selected;
  const start = trip.startDate || trip.start_date;
  const end = trip.endDate || trip.end_date || start;
  const dated = stage !== 'idea' && !!start;
  const place = center.name || tripPlaceName(trip);
  const stay = center.stay || trip.stay;
  const stayName = center.kind === 'stay' && stay?.name && !stay.is_home ? stay.name : null;

  const subtitle = dated
    ? (stayName
      ? t('away.subtitleStay', { dates: formatDateRangeShort(start, end), stay: stayName })
      : t('away.subtitleDated', { dates: formatDateRangeShort(start, end), place }))
    : t('away.subtitleIdea', { place });

  const chips = away.slice(0, CHIP_LIMIT);
  const items = (suppressKeys?.size ? ideas.items.filter(i => !suppressKeys.has(feedItemKey(i))) : ideas.items).slice(0, CARD_LIMIT);
  const hasPlaces = items.some(i => !EVENT_TYPES.has(autoNormalizeCardData(i).type));

  const handleAdded = (added) => {
    const dayNum = tripDayNumber(trip, added.dayDate) || (added.dayIndex || 0) + 1;
    showToast(t('away.added', { day: dayNum, trip: trip.name }));
    if (adding?.key) setAddedByKey(prev => ({ ...prev, [adding.key]: dayNum }));
    invalidateDashboardCache(currentUser?.id);
    invalidatePlannerCache(currentUser?.id);
    invalidateTripCache(trip.id);
    onAdded?.();
  };

  return (
    <section ref={sectionRef} aria-labelledby="home-away-heading" className="scroll-mt-20">
      <HomeSectionHeader
        id="home-away-heading"
        title={t('away.title', { place })}
        subtitle={subtitle}
        action={chips.length > 1 && (
          <div className="hidden md:flex gap-2">
            {chips.map(entry => <TripChip key={entry.trip.id} entry={entry} selected={entry.trip.id === selected.trip.id} onSelect={setSelectedId} />)}
          </div>
        )}
      />
      {chips.length > 1 && (
        <div className="flex gap-2 overflow-x-auto scrollbar-hide -mx-4 px-4 pb-3 md:hidden">
          {chips.map(entry => <TripChip key={entry.trip.id} entry={entry} selected={entry.trip.id === selected.trip.id} onSelect={setSelectedId} />)}
        </div>
      )}

      {ideas.loading ? <Skeleton /> : ideas.error ? (
        <EmptyState.Error onRetry={ideas.retry} />
      ) : items.length === 0 ? (
        <p className="text-sm text-content-secondary">{t('away.empty', { place })}</p>
      ) : (
        <>
          <CardGroup cols={4} className="animate-fade-in">
            {items.map((item, i) => {
              const key = feedItemKey(item) || `${item.content_type}-${item.id || i}`;
              return (
                <div key={key} className={STRIP_SLOT}>
                  <IdeaCard
                    item={item}
                    trip={trip}
                    dated={dated}
                    today={today}
                    added={addedByKey[`${trip.id}|${key}`] || null}
                    onAdd={(activity, date, eventStart) => setAdding({ activity, date, eventStart, key: `${trip.id}|${key}` })}
                    onSave={(raw) => buildActions(raw).onSave?.()}
                  />
                </div>
              );
            })}
          </CardGroup>
          {/* Place cards can carry Google photos (docs/GOOGLE_ATTRIBUTION.md rule 2). */}
          {hasPlaces && <PoweredByGoogle variant="slim" align="end" className="mt-1" />}
        </>
      )}

      <Suspense fallback={null}>
        {adding && (
          <AddToTripModal
            isOpen
            onClose={() => setAdding(null)}
            activity={adding.activity}
            tripDays={[]}
            tripId={trip.id}
            tripName={trip.name}
            preSelectedDayDate={adding.date}
            eventStart={adding.eventStart}
            useApi
            onApiSuccess={handleAdded}
            onApiError={(err) => console.error('Failed to add to trip day:', err)}
          />
        )}
      </Suspense>
      {toast && <Toast message={toast.message} variant={toast.variant} onClose={hideToast} />}
    </section>
  );
}

function TripChip({ entry, selected, onSelect }) {
  const { t } = useTranslation('home');
  const { trip, center, stage } = entry;
  const start = trip.startDate || trip.start_date;
  const end = trip.endDate || trip.end_date || start;
  const label = stage === 'idea' || !start
    ? t('away.chipNoDates', { place: center.name || tripPlaceName(trip) })
    : t('away.chipDated', { place: center.name || tripPlaceName(trip), dates: formatDateRangeShort(start, end) });
  return (
    <button
      type="button"
      onClick={() => onSelect(trip.id)}
      aria-pressed={selected}
      className={`flex-shrink-0 h-9 px-3.5 rounded-full text-sm font-medium border transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light ${selected ? 'bg-primary border-primary text-content-inverse' : 'bg-surface border-border text-content-primary can-hover:hover:border-content-muted'}`}
    >
      {label}
    </button>
  );
}
